import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import {
  Check,
  Clear,
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Comment, CommentResponse } from '../types/campagne';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface CommentSectionProps {
  comments: Comment[];
  onAddComment: (text: string) => void;
  onAddResponse: (commentId: string, text: string) => void;
  onResolveComment: (commentId: string) => void;
  onDeleteComment: (commentId: string) => void;
  isAdmin?: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  comments = [],
  onAddComment,
  onAddResponse,
  onResolveComment,
  onDeleteComment,
  isAdmin = false,
}) => {
  const [newComment, setNewComment] = useState('');
  const [newResponses, setNewResponses] = useState<{ [key: string]: string }>({});

  const handleAddComment = () => {
    if (newComment.trim()) {
      onAddComment(newComment);
      setNewComment('');
    }
  };

  const handleAddResponse = (commentId: string) => {
    const response = newResponses[commentId];
    if (response?.trim()) {
      onAddResponse(commentId, response);
      setNewResponses(prev => ({ ...prev, [commentId]: '' }));
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Commentaires
      </Typography>

      {/* Zone d'ajout de commentaire */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Ajouter un commentaire..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleAddComment}
          disabled={!newComment.trim()}
        >
          Commenter
        </Button>
      </Box>

      {/* Liste des commentaires */}
      <List>
        {(comments || []).map((comment) => (
          <ListItem
            key={comment.id}
            sx={{
              flexDirection: 'column',
              alignItems: 'stretch',
              mb: 2,
              opacity: comment.isResolved ? 0.7 : 1,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: comment.isResolved ? 'action.hover' : 'background.paper',
              }}
            >
              {/* En-tête du commentaire */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  {comment.author}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(comment.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                  </Typography>
                  {isAdmin && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => onResolveComment(comment.id)}
                        color={comment.isResolved ? "success" : "default"}
                      >
                        {comment.isResolved ? <Check /> : <Clear />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteComment(comment.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              {/* Contenu du commentaire */}
              <Typography variant="body1">{comment.text}</Typography>

              {/* Réponses */}
              {comment.responses && comment.responses.length > 0 && (
                <List sx={{ ml: 3, mt: 1 }}>
                  {comment.responses.map((response) => (
                    <ListItem key={response.id} sx={{ px: 0 }}>
                      <Paper elevation={0} sx={{ p: 1, backgroundColor: 'action.hover', width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="subtitle2" color="primary">
                            {response.author}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(response.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{response.text}</Typography>
                      </Paper>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Zone de réponse */}
              {!comment.isResolved && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Répondre..."
                    value={newResponses[comment.id] || ''}
                    onChange={(e) =>
                      setNewResponses(prev => ({
                        ...prev,
                        [comment.id]: e.target.value
                      }))
                    }
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleAddResponse(comment.id)}
                    disabled={!newResponses[comment.id]?.trim()}
                  >
                    Répondre
                  </Button>
                </Box>
              )}
            </Paper>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}; 